import React from 'react';
import { FeaturesType } from 'contexts/features';
import Link from 'components/link';
import ImageLoader from 'components/image-loader';
import TwitterIcon from 'components/icon/twitter-icon';
import FacebookIcon from 'components/icon/facebook-icon';
import InstagramIcon from 'components/icon/instagram-icon';
import LinkedInIcon from 'components/icon/linkedin-icon';
import MailIcon from 'components/icon/mail-icon';
import PhoneIcon from 'components/icon/phone-icon';
import YoutubeIcon from 'components/icon/youtube-icon';
import styles from './expRealtyFooterStyle.module.scss';
import { buildClassName } from 'utils/build-class-name';
import Button, { SECONDARY_THEME } from 'components/control/button';
import { openEqualWebMenu } from 'utils/accessibility';
import { EXP_REALTY_CA_TENANT_ID_TYPE, EXP_REALTY_US_TENANT_ID_TYPE, FooterColItem, FooterConfig, FooterImageData, OrganizationConfig } from '@tenants/types/tenant';
import { CountryCode } from 'types/countries';
import { useTranslation } from 'next-i18next';
import { LOCALE_EXP_REALTY_CA_PATHS, LOCALE_EXP_REALTY_US_PATHS } from 'types/locales';
import { EXP_REALTY_CA_TENANT_ID, EXP_REALTY_US_TENANT_ID } from '@tenants/types';

type ExpRealtyTenantId = EXP_REALTY_CA_TENANT_ID_TYPE | EXP_REALTY_US_TENANT_ID_TYPE;

export type ExpRealtyFooterProps = {
  config: FooterConfig;
  organizationConfig: OrganizationConfig;
  tenantId: ExpRealtyTenantId;
  countryCode: CountryCode;
  features?: FeaturesType;
};

export type ExpRealtyFooterDebugProps = {
  features?: FeaturesType;
};

export default function ExpRealtyFooter({ config, organizationConfig, features: _features, tenantId, countryCode }: ExpRealtyFooterProps) {
  const { t } = useTranslation(tenantId);

  const address = organizationConfig.address(countryCode);
  const phoneNumber = organizationConfig.phoneNumber();
  const emailAddress = organizationConfig.email();
  const { twitterUrl, facebookUrl, linkedinUrl, instagramUrl, youtubeUrl } = organizationConfig;
  const { column1 = [], column2 = [], column3 = [], bottom = [], joinUsLink, footerImageData } = config.footerColumns;
  return (
    <div className={styles.component} data-testid={`${tenantId}-footer`}>
      <div className={styles['footer-top']}>
        <div className={styles['footer-top-left']}>
          <div className={styles['footer-links']}>
            <div className={buildClassName(styles.block, styles.block1)}>
              {/* TODO: Translate column contents in the parent component instead of passing tenant ID to each column */}
              <Column1 items={column1} tenantId={tenantId}/>
            </div>
            <div className={buildClassName(styles.block, styles.block2)}>
              <Column2 items={column2} supportsAccessibility={config.showAccessibilityLink} tenantId={tenantId} />
            </div>
            <div className={buildClassName(styles.block, styles.block3)}>
              <Column3 items={column3} tenantId={tenantId} />
            </div>
            <div className={buildClassName(styles.block, styles.block4)}>
              <p className={styles['office-address']}>
                {address}
              </p>
              <EmailPhoneNumberContacts phoneNumber={phoneNumber} emailAddress={emailAddress}/>
              <div className={styles.socials}>
                <a href={twitterUrl} target="_blank" rel="noreferrer"><TwitterIcon isExpTheme className={styles['social-x-icon']}/></a>
                <a href={facebookUrl} target="_blank" rel="noreferrer"><FacebookIcon isExpTheme className={styles['social-x-icon']}/></a>
                <a href={linkedinUrl} target="_blank" rel="noreferrer"><LinkedInIcon isExpTheme className={styles['social-x-icon']}/></a>
                <a href={instagramUrl} target="_blank" rel="noreferrer"><InstagramIcon isExpTheme className={styles['social-ig-icon']}/></a>
                <a href={youtubeUrl} target="_blank" rel="noreferrer"><YoutubeIcon className={styles['social-ig-icon']}/></a>
              </div>
            </div>
          </div>
          <div className={styles['footer-big-text']}>
            <p>
              eXp REALTY
            </p>
            <div className={buildClassName(styles['join-section-mobile-container'])}>
              <JoinSection joinUsLink={joinUsLink} footerImageData={footerImageData} tenantId={tenantId} />
            </div>
          </div>
        </div>
        <div className={buildClassName(styles['footer-top-right'])}>
          <JoinSection joinUsLink={joinUsLink} footerImageData={footerImageData} tenantId={tenantId} />
        </div>
      </div>
      <div className={styles['footer-bottom']}>
        { bottom.map((section, index) => {
          return (
            <p key={index}>
              {section.map(({ isAnchor, link, target, label }, innerIndex) => {
                return (
                  <span key={innerIndex}>
                    {
                      isAnchor ?
                        <a key={index} href={link} target={target}>{t(label)}</a>
                        :
                        <>{t(label)}</>
                    }
                  </span>
                );
              })}
            </p>
          );
        })}
        <ExpDynamicCopyright/>
      </div>
    </div>
  );
}

const ExpDynamicCopyright = () =>
  <p> © {new Date().getFullYear()} eXp Realty. eXp World Holdings, Inc. All Rights Reserved</p>;

const handleAccessibilityClick = () => {
  openEqualWebMenu();
};

const EmailPhoneNumberContacts = ({ phoneNumber, emailAddress }: { phoneNumber: string; emailAddress: string }) => {

  return (
    <section className={styles.contact}>
      {phoneNumber && (
        <div className={styles['contact-item']}>
          <a href={`tel:${phoneNumber}`}>
            <PhoneIcon/>
            <p>
              {phoneNumber}
            </p>
          </a>
        </div>
      )}
      {emailAddress && (
        <div className={styles['contact-item']}>
          <a href={`mailto:${emailAddress}`}>
            <MailIcon/>
            <p>
              {emailAddress}
            </p>
          </a>
        </div>
      )}
    </section>
  );
};

const Column1 = ({ items, tenantId }: { items: ReadonlyArray<FooterColItem>; tenantId: ExpRealtyTenantId }) => {
  const { t } = useTranslation(tenantId);
  return (
    <div className={styles['about-exp-links']}>
      {
        items.map(({ isAnchor, link, target, label }, index) => {
          return (isAnchor ?
            <a key={`column1-${index}`} href={link} target={target}>{t(label)}</a>
            :
            <Link key={`column1-${index}`} href={link} target={target}>{t(label)}</Link>);
        })
      }
    </div>
  );
};

const Column2 = ({ items, supportsAccessibility, tenantId }: { items: ReadonlyArray<FooterColItem>; supportsAccessibility: boolean; tenantId: ExpRealtyTenantId }) => {
  const { t } = useTranslation(tenantId);
  return (
    <div className={styles['about-exp-links']}>
      {
        items.map(({ isAnchor, link, target, label }, index) => {
          if (link === '/accessibility' && supportsAccessibility) {
            return (<a key={`column2-${index}`} onClick={handleAccessibilityClick}>Accessibility</a>);
          }
          return (isAnchor ?
            <a key={`column2-${index}`} href={link} target={target}>{t(label)}</a>
            :
            <Link key={`column2-${index}`} href={link} target={target}>{t(label)}</Link>);
        })
      }
    </div>
  );
};

const Column3 = ({ items, tenantId }: { items: ReadonlyArray<FooterColItem>; tenantId: ExpRealtyTenantId }) => {
  const { t } = useTranslation(tenantId);
  return (
    <div className={buildClassName(styles['about-exp-links'], styles['terms-and-conditions'])}>
      {
        items.map(({ isAnchor, link, target, label }, index) => {
          return (isAnchor ?
            <a key={`column3-${index}`} href={link} target={target}>{t(label)}</a>
            :
            <Link key={`column3-${index}`} href={link} target={target}>{t(label)}</Link>);
        })
      }
    </div>
  );
};

const getExploreExpLabelPath = (tenantId: ExpRealtyTenantId) => {
  switch (tenantId) {
    case EXP_REALTY_CA_TENANT_ID:
      return LOCALE_EXP_REALTY_CA_PATHS.FOOTER_EXPLORE_EXP_LABEL;
    case EXP_REALTY_US_TENANT_ID:
      return LOCALE_EXP_REALTY_US_PATHS.FOOTER_EXPLORE_EXP_LABEL;
    default:
      return LOCALE_EXP_REALTY_CA_PATHS.FOOTER_EXPLORE_EXP_LABEL; // fallback
  }
};

const JoinSection = ({ joinUsLink, footerImageData, tenantId }: { joinUsLink: string; footerImageData: FooterImageData; tenantId: ExpRealtyTenantId }) => {
  const { t } = useTranslation(tenantId);
  const exploreExpLabelPath = getExploreExpLabelPath(tenantId);
  
  return (
    <div className={styles['join-section']}>
      <div className={styles['join-image-container']}>
        <ImageLoader src={footerImageData.src} alt={footerImageData.alt}/>
      </div>
      <Link href={joinUsLink} target='_blank' className={styles['join-link']}>
        <Button theme={SECONDARY_THEME} label={t(exploreExpLabelPath, {
          defaultValue: 'Explore eXp',
        })} className={styles['join-button']} />
      </Link>
    </div>
  );
};
